/**
 * MusicDirectorAdapter — game-side adapter managing two HowlLike slots for
 * crossfade ping-pong. Reads effective volumes from AudioBus.
 *
 * Two slots (A / B) are used as a double-buffer: one holds the outgoing track,
 * the other the incoming track. When a crossfade finishes, the outgoing slot
 * is stopped and released so the next crossfade can claim it.
 *
 * No Phaser import — fully testable in Node/Vitest with mock HowlLike objects.
 *
 * TODO(MY-1974): When Howler lands (MY-1429), swap the default factory:
 *   import { Howl } from 'howler';
 *   import { musicPath } from '@/data/musicManifest';
 *   const howlerFactory: MusicFactory = (key) =>
 *     new Howl({ src: [musicPath(key)], onloaderror: (_, e) =>
 *       console.warn(`[MusicDirectorAdapter] Missing: ${key}`, e) });
 */

import { AudioBus, NoopHowl, type HowlLike } from '@/systems/AudioBus';
import { CombatDuck } from '@/systems/CombatDuck';
import { MusicDirector } from '@/systems/MusicDirector';
import { MUSIC_MANIFEST, type MusicKey, type UIScene } from '@/data/musicManifest';

/** Creates a HowlLike handle for a given music key. */
export type MusicFactory = (key: MusicKey) => HowlLike;

interface MusicSlot {
  key: MusicKey;
  howl: HowlLike;
}

export class MusicDirectorAdapter {
  private readonly director: MusicDirector;
  private readonly bus: AudioBus;
  private readonly factory: MusicFactory;
  private readonly combatDuck: CombatDuck;

  /** Two audio slots for crossfade ping-pong (outgoing / incoming). */
  private slotA: MusicSlot | null = null;
  private slotB: MusicSlot | null = null;

  constructor(
    bus?: AudioBus,
    factory: MusicFactory = () => new NoopHowl(),
    director?: MusicDirector,
    combatDuck?: CombatDuck,
  ) {
    this.bus = bus ?? new AudioBus();
    this.factory = factory;
    this.director = director ?? new MusicDirector(MUSIC_MANIFEST);
    this.combatDuck = combatDuck ?? new CombatDuck();
  }

  // ── Events (forward to MusicDirector) ───────────────────────────────────────

  startStage(stageId: number): void {
    this.director.handleStageStart(stageId);
  }

  onCheckpoint(): void {
    this.director.handleCheckpoint();
  }

  onBossTriggered(bossId: string): void {
    this.director.handleBossTriggered(bossId);
    this.combatDuck.startDuck();
  }

  onBossDefeated(): void {
    this.director.handleBossDefeated();
    this.combatDuck.stopDuck();
  }

  onSceneChange(scene: UIScene): void {
    this.director.handleSceneChange(scene);
  }

  // ── Frame update ─────────────────────────────────────────────────────────────

  /**
   * Advance the crossfade and apply volumes. Call once per frame with the
   * scene's delta time in milliseconds.
   */
  update(deltaMs: number): void {
    const state = this.director.tick(deltaMs);
    const duckMult = this.combatDuck.tick(deltaMs);
    const base = this.bus.getEffectiveVolume('music') * duckMult;

    if (state.incoming !== null) {
      const slot = this._ensureSlot(state.incoming.key);
      slot.howl.volume(state.incoming.volume * base);
    }

    if (state.outgoing !== null) {
      const slot = this._getSlot(state.outgoing.key);
      if (slot !== null) {
        slot.howl.volume(state.outgoing.volume * base);
      }
    }

    if (state.crossfadeDone) {
      this._releaseOutgoing(state.incoming?.key ?? null);
    }
  }

  /**
   * Re-apply current bus volume to all active slots.
   * Call after bus setVolume/setMute changes (e.g., from options screen).
   */
  applyBusVolume(): void {
    const base = this.bus.getEffectiveVolume('music') * this.combatDuck.multiplier;
    if (this.slotA !== null) this.slotA.howl.volume(base);
    if (this.slotB !== null) this.slotB.howl.volume(base);
  }

  /** Stop all music immediately and release both slots. */
  stopAll(): void {
    this.slotA?.howl.stop();
    this.slotA = null;
    this.slotB?.howl.stop();
    this.slotB = null;
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  /**
   * Return the slot for key, starting a new one if needed.
   * New slots start at volume 0 so the crossfade curve controls the ramp-in.
   */
  private _ensureSlot(key: MusicKey): MusicSlot {
    if (this.slotA?.key === key) return this.slotA;
    if (this.slotB?.key === key) return this.slotB;

    const howl = this.factory(key);
    howl.loop(true);
    howl.volume(0);
    howl.play();

    const slot: MusicSlot = { key, howl };
    if (this.slotA === null) {
      this.slotA = slot;
    } else if (this.slotB === null) {
      this.slotB = slot;
    } else {
      // Both slots occupied (shouldn't happen in normal two-track crossfade).
      // Evict whichever slot is not the current director key.
      const currentKey = this.director.currentKey;
      if (this.slotA.key !== currentKey) {
        this.slotA.howl.stop();
        this.slotA = slot;
      } else {
        this.slotB.howl.stop();
        this.slotB = slot;
      }
    }
    return slot;
  }

  private _getSlot(key: MusicKey): MusicSlot | null {
    if (this.slotA?.key === key) return this.slotA;
    if (this.slotB?.key === key) return this.slotB;
    return null;
  }

  /** Stop and release any slot that does not hold keepKey. */
  private _releaseOutgoing(keepKey: MusicKey | null): void {
    if (this.slotA !== null && this.slotA.key !== keepKey) {
      this.slotA.howl.stop();
      this.slotA = null;
    }
    if (this.slotB !== null && this.slotB.key !== keepKey) {
      this.slotB.howl.stop();
      this.slotB = null;
    }
  }
}
